import { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';

interface Contact {
  id: string;
  name: string;
  // 'promoter' / 'service_provider' rimangono per compatibilità con dati legacy
  role: 'artist' | 'organizer' | 'booking_manager' | 'venue_manager' | 'other' | 'promoter' | 'service_provider';
}

interface UserProfile {
  id: string;
  user_id: string;
  app_role?: 'artist' | 'booking_manager' | 'venue_manager' | 'accountant' | 'admin';
  contact_id?: string;
  language: string;
  timezone: string;
  currency: string;
  contacts?: {
    name: string;
  };
}

type AppRole = 'artist' | 'booking_manager' | 'venue_manager' | 'accountant' | 'admin';

interface UserProfileContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  userRoles: AppRole[];
  userContacts: Contact[];
  isAdmin: boolean;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType>({
  user: null,
  session: null,
  userProfile: null,
  userRoles: [],
  userContacts: [],
  isAdmin: false,
  isLoading: true,
  refreshProfile: async () => {},
});

export const useUserProfile = () => useContext(UserProfileContext);

export function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [userContacts, setUserContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const userIdRef = useRef<string | null>(null);
  
  // Keep ref in sync with current user id
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);
  
  const isAdmin = useMemo(() => {
    return userRoles.includes('admin');
  }, [userRoles]);

  const fetchProfile = async (userId: string) => {
    setIsLoading(true);
    
    // Only invalidate caches on explicit refresh, not on initial load
    // This reduces unnecessary queries
    
    try {
      // Fetch user profile without JOIN to avoid RLS issues
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (!error && data) {
        setUserProfile(data);
      }

      // Fetch user roles using the security definer function
      let finalRoles: AppRole[] = [];
      
      try {
        const { data: rolesData, error: rolesError } = await supabase
          .rpc('get_user_roles', { _user_id: userId });

        if (!rolesError && rolesData) {
          if (Array.isArray(rolesData) && rolesData.length > 0) {
            const firstItem = rolesData[0] as any;
            
            // Check if it's wrapped format: [{get_user_roles: 'artist'}]
            if (firstItem && typeof firstItem === 'object' && firstItem !== null && 'get_user_roles' in firstItem) {
              finalRoles = rolesData.map((item: any) => item.get_user_roles as AppRole);
            } 
            // Check if it's direct string array: ['artist', 'booking_manager']
            else if (typeof firstItem === 'string') {
              finalRoles = rolesData as AppRole[];
            }
          }
        } else {
          // Fallback: try direct query
          const { data: directRoles, error: directError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId);
          
          if (!directError && directRoles) {
            finalRoles = directRoles.map(r => r.role as AppRole);
          }
        }
      } catch (rolesException) {
        logger.error('Error fetching roles:', rolesException);
      }

      setUserRoles(finalRoles);

      // Fetch contacts associated with user email
      const { data: userEmailData } = await supabase.auth.getUser();
      if (userEmailData?.user?.email) {
        const { data: contactsData } = await supabase
          .from('contacts')
          .select('id, name, role')
          .eq('user_email', userEmailData.user.email)
          .order('role');

        if (contactsData) {
          setUserContacts(contactsData as Contact[]);
        } else {
          setUserContacts([]);
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      logger.error('Error in fetchProfile:', error);
      setUserRoles([]);
      setUserContacts([]);
      setIsLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      // Invalidate caches only on explicit refresh
      queryClient.invalidateQueries({ queryKey: ['concerts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (!mounted) return;
      
      // For token refreshes, skip re-render if user hasn't changed
      if (event === 'TOKEN_REFRESHED') {
        logger.info('Token refreshed successfully');
        if (currentSession?.user?.id !== userIdRef.current) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
        return;
      }
      
      // Don't auto-logout on SIGNED_OUT if caused by network error
      if (event === 'SIGNED_OUT' && currentSession === null) {
        const storedSession = localStorage.getItem('sb-iayzvlndqdceqptguirk-auth-token');
        if (storedSession) {
          supabase.auth.getSession();
          return;
        }
      }
      
      // Skip state updates if user hasn't changed (prevents re-renders on tab focus)
      const newUserId = currentSession?.user?.id ?? null;
      if (newUserId === userIdRef.current && newUserId !== null) {
        return;
      }

      // User actually changed (login, logout, account switch):
      // wipe per-user query caches to avoid showing previous user's data
      // (e.g. empty 'tasks' cache leaking across sessions).
      queryClient.removeQueries({ queryKey: ['tasks'] });

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        setTimeout(() => {
          if (mounted) fetchProfile(currentSession.user.id);
        }, 0);
      } else {
        setUserProfile(null);
        setUserRoles([]);
        setUserContacts([]);
        setIsLoading(false);
      }
    });

    // Initial session recovery
    supabase.auth.getSession().then(({ data: { session: currentSession }, error }) => {
      if (!mounted) return;
      
      if (error) {
        // Clean up invalid token and require fresh login
        if (error.message?.includes('refresh_token_not_found')) {
          localStorage.removeItem('sb-iayzvlndqdceqptguirk-auth-token');
          logger.warn('Invalid refresh token cleared');
        }
        setIsLoading(false);
        return;
      }
      
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const contextValue = useMemo(
    () => ({ user, session, userProfile, userRoles, userContacts, isAdmin, isLoading, refreshProfile }),
    [user, session, userProfile, userRoles, userContacts, isAdmin, isLoading],
  );

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
}
